import {
  ComponentType,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from 'react';

import { FeatureFlaggedProps as BackstageFeatureFlaggedProps } from '@backstage/core-app-api';

import { useFlag } from '@spotify-confidence/react';
import { Value } from '@spotify-confidence/sdk';

import { useLocalOverride } from './overrideStore';

export const useFeatureFlag = <T extends Value>(
  flag: string,
  defaultValue: T,
): T => useLocalOverride(flag, useFlag(flag, defaultValue));

type NamedFeatureFlaggedProps = PropsWithChildren<{ name: string }>;

export type FeatureFlaggedProps =
  | BackstageFeatureFlaggedProps
  | NamedFeatureFlaggedProps;

const isNamed = (
  props: FeatureFlaggedProps,
): props is NamedFeatureFlaggedProps => 'name' in props;

const flagOf = (props: FeatureFlaggedProps): string => {
  if (isNamed(props)) {
    return props.name;
  }
  return 'with' in props ? props.with : props.without;
};

// Undefined outside a named <FeatureFlagged>, which is how On and Off tell
// they have no root to read a flag from.
const FlagEnabledContext = createContext<boolean | undefined>(undefined);

const useFlagEnabled = (branch: 'On' | 'Off'): boolean => {
  const enabled = useContext(FlagEnabledContext);
  if (enabled === undefined) {
    throw new Error(
      `<FeatureFlagged.${branch}> must be inside <FeatureFlagged name="...">.`,
    );
  }
  return enabled;
};

const On = ({ children }: PropsWithChildren) => (
  <>{useFlagEnabled('On') ? children : null}</>
);
On.displayName = 'FeatureFlagged.On';

const Off = ({ children }: PropsWithChildren) => (
  <>{useFlagEnabled('Off') ? null : children}</>
);
Off.displayName = 'FeatureFlagged.Off';

/**
 * Renders children based on a feature flag, in one of two forms.
 *
 * One branch takes the flag inline:
 *
 *     <FeatureFlagged with="my-flag">…</FeatureFlagged>
 *     <FeatureFlagged without="my-flag">…</FeatureFlagged>
 *
 * Two alternative branches name the flag once and split on it, so the flag
 * cannot drift between them:
 *
 *     <FeatureFlagged name="my-flag">
 *       <FeatureFlagged.On>…</FeatureFlagged.On>
 *       <FeatureFlagged.Off>…</FeatureFlagged.Off>
 *     </FeatureFlagged>
 */
const FeatureFlaggedRoot = (props: FeatureFlaggedProps) => {
  // One unconditional hook call. Which prop carries the flag differs
  // between the two forms, but a flag is always read.
  const { enabled } = useFeatureFlag(flagOf(props), { enabled: false });

  if (isNamed(props)) {
    return (
      <FlagEnabledContext.Provider value={enabled}>
        {props.children}
      </FlagEnabledContext.Provider>
    );
  }

  const desired = 'with' in props;
  return <>{enabled === desired ? props.children : null}</>;
};

export const FeatureFlagged = Object.assign(FeatureFlaggedRoot, { On, Off });

export const splitComponent = <
  V extends Value.Struct,
  P extends Record<string, unknown> = Record<string, unknown>,
>(
  flag: string,
  defaultValue: V,
  factory: (value: V) => ComponentType<P>,
) => {
  const SplitComponent = (props: P) => {
    const value = useFeatureFlag(flag, defaultValue);
    const Component = useMemo(
      () => (value ? factory(value as V) : null),
      [value],
    );
    if (!Component) {
      return null;
    }

    return <Component {...props} />;
  };
  SplitComponent.displayName = `splitComponent(${flag})`;
  return SplitComponent;
};
